import React, { useMemo } from 'react'

import { ApolloProvider } from '@apollo/client'
import { t } from 'src/translations'
import { ContainsEditableProps, useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'

import { MenuItemIdentifier } from 'src/apollo/sites'
import { formatImageURL } from 'src/lib/js/utilities'

import Image from 'shared/components/common/Image'
import { useBulkCalls } from 'shared/components/common/dynamic_menus/BulkCallsContext'
import Link from 'shared/components/common/link'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import PriceFromRange from 'shared/components/common/price/PriceFromRange'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import {
  getMenuPath,
  getOrderPath
} from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

import MenuCardSkeleton from './MenuCardSkeleton'
import {
  getMenuItemSrcSetInProd,
  menuItemPagePath,
  menuItemPath
} from 'public/components/default_template/menu_section/MenuItemCard'
import {
  getBorderStyle,
  getBoxShadowStyle,
  getBorderRadiusStyle
} from 'src/shared/utils/stylingUtils'
import { BorderAndShadowConfig } from 'src/apollo/sites'

type TestProps = { testId?: string }

export type MenuItemBlockBackupContent = {
  name: string
  description: string
  price: number | string | null
  imageSrc: string
  itemTags?: { guid: string; name: string }[]
  prices?: { price: number }[]
}

type MenuItemBlockConfig = {
  hideItemName: boolean
  hideItemPrice: boolean
  hideItemDescription: boolean
}

type EditableProps = {
  classNames?: string
  editableRef?: any
  id: MenuItemIdentifier
  backupContent: MenuItemBlockBackupContent
  config: MenuItemBlockConfig | null
  borderAndShadowConfig?: BorderAndShadowConfig | null
  overrideGlobalBorderAndShadowConfig?: boolean | null
  styles?: any
} & TestProps &
  ContainsEditableProps

const EditableMenuItem = ({ styles = {}, ...props }: EditableProps) => {
  const { editPath, id, backupContent } = props
  const { useEditableRef } = useEditor()
  const { menuItems, loading } = useBulkCalls()

  const { restaurant, locations } = useRestaurant()
  const designConfig = restaurant?.config?.designConfig?.borderAndShadowConfig
  const hasOoPage =
    restaurant.config?.isOnlineOrderingEnabled &&
    !restaurant.config.ooConfig?.optedOut

  const { editableRef } = useEditableRef<HTMLDivElement>({
    name: 'menu item',
    displayName: 'Menu Item',
    actions: ['delete'],
    path: editPath,
    schema: { fields: [] }
  })

  const item = useMemo(() => {
    return menuItems?.find((item) => item.guid === id?.itemGuid)
  }, [menuItems, id?.itemGuid])

  const imgSrc = useMemo(() => {
    if (item?.imageLink) {
      if (item?.imageLink.startsWith('https://toast-stage.s3.amazonaws.com/')) {
        return item?.imageLink.replace(
          'https://toast-stage.s3.amazonaws.com/',
          'https://s3.amazonaws.com/toasttab/'
        )
      }
      return item?.imageLink
    }
    return backupContent?.imageSrc
  }, [item?.imageLink, backupContent?.imageSrc])

  const compositeConfig = useMemo(() => {
    // null for override = false (use global)
    if (!props.overrideGlobalBorderAndShadowConfig) {
      return designConfig
    }

    if (props.overrideGlobalBorderAndShadowConfig === true) {
      return props.borderAndShadowConfig
    }

    return null
  }, [
    designConfig,
    props.borderAndShadowConfig,
    props.overrideGlobalBorderAndShadowConfig
  ])

  const borderAndShadowStyle = useMemo(() => {
    return {
      ...getBorderStyle(compositeConfig),
      ...getBoxShadowStyle(compositeConfig),
      ...getBorderRadiusStyle(compositeConfig, 16)
    }
  }, [compositeConfig])

  const itemImage = useMemo(() => {
    if (imgSrc) {
      return (
        <Image
          alt={item?.name || ''}
          src={formatImageURL(imgSrc)}
          // TODO(https://toasttab.atlassian.net/browse/WOO-3775): Also add sizes prop.
          srcSet={getMenuItemSrcSetInProd(item?.imageUrls)}
          className={classnames('itemImage')}
        />
      )
    }

    return null
  }, [imgSrc, item?.name, item?.imageUrls])

  const itemLink = useMemo(() => {
    if (!hasOoPage || !locations) {
      return null
    }

    // If there is more than one location, don't assume which
    // location the user would like to see.
    if (locations.length !== 1) {
      return '/order'
    }

    // If there is just one location, link directly to the item.
    if (locations[0]?.shortUrl && id?.itemGuid && item?.name) {
      return id?.itemGroupGuid
        ? menuItemPagePath(
            getMenuPath(locations[0]?.shortUrl),
            id.itemGroupGuid,
            item?.name,
            id?.itemGuid
          )
        : menuItemPath(
            getOrderPath(locations[0]?.shortUrl),
            item?.name,
            id?.itemGuid
          )
    }

    // Fallback to the order page if there is a data issue.
    return '/order'
  }, [hasOoPage, locations, id?.itemGuid, id?.itemGroupGuid, item?.name])

  if (loading) {
    return (
      <MenuCardSkeleton
        hideItemName={props.config?.hideItemName}
        hideItemPrice={props.config?.hideItemPrice}
        hideItemDescription={props.config?.hideItemDescription}
      />
    )
  }

  const name = item?.name || backupContent?.name || t('menu-item.item-name')
  const description =
    item?.description ||
    backupContent?.description ||
    t('menu-item.item-description')

  // Get the price from item or fallback to backup content
  const price: number | string | null | undefined =
    item?.price !== undefined ? item?.price : backupContent?.price

  const menuItemContent = (
    <div className='editableMenuItemContainer' ref={editableRef}>
      <div
        className={'menuItemWrapper'}
        style={{ ...styles, ...borderAndShadowStyle }}
      >
        {itemImage}
        <div className='contentDiv'>
          {!props.config?.hideItemName && <h2 className='itemName'>{name}</h2>}
          {!props.config?.hideItemDescription && (
            <p className='itemDescription'>{description}</p>
          )}
          {!props.config?.hideItemPrice && (price != null || item?.prices) && (
            <span className='itemPrice'>
              <PriceFromRange price={price} prices={item?.prices} />
            </span>
          )}
        </div>
      </div>
    </div>
  )

  if (itemLink) {
    return (
      <Link href={itemLink} className='menuItemLink'>
        {menuItemContent}
      </Link>
    )
  }
  return menuItemContent
}

const WrappedEditableMenuItem = (props: EditableProps) => {
  const ooClient = useOOClient()
  return (
    <ApolloProvider client={ooClient}>
      <EditableMenuItem {...props} />
    </ApolloProvider>
  )
}

export default WrappedEditableMenuItem
